import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDDPSubscription, useDDPQuery } from '@zedoc/ddp-connector';
import { Random } from '@zedoc/random';
import {
  apiZedocOneProject,
  patientDetails,
  projectMilestones,
} from '../../../common/api/zedoc';
import { getQuestionnaireNames } from '../../../common/api/aggregations/Projects';
import { default as ParticipationSelect } from '../../../common/selectors/Participation';
import { default as ProjectSelect } from '../../../common/selectors/Project';
import { default as ProjectMilestoneSelect } from '../../../common/selectors/ProjectMilestone';
import {
  setMilestoneJsonSchemaGroupedByProject,
  setProjectJsonSchema,
  setQuestionnaireJsonSchema,
  setTimezone,
} from '../../../utils/useProjectFilters';
import {
  getDocumentsFilters,
  setDocumentsFilters,
} from '../../../screens/PatientCard/store';
import {
  getDocumentsDefaultProjectId,
  clearDocumentsDefaultProjectId,
} from '../../../utils/documentsDefaultProjectId';
import defaultFilters from './defaultFilters';
import FiltersToolbar from './FiltersToolbar';
import { getSelectedProjectIds, hasProjectFilter } from './filterHelpers';
import { FILTER_TYPE__ALL_OF } from '../../../common/constants';

// The "Project" preset as authored in defaultFilters.js — the template for the
// filters applied programmatically below, so they carry the exact same meta
// (conditions/category/noSearch) a manually-selected Project filter has. Read
// from the static preset list (not the schema-enriched `presets` computed
// further down) so it never depends on project data having loaded yet.
const projectFilterPreset = find(
  defaultFilters,
  (preset) => preset.settings && preset.settings.id === 'projectId',
);

// Mirrors what picking "Project" from the segment dropdown produces: the leaf
// preset wrapped in the ALL_OF group that elements/Filters.js expects. A bare
// leaf renders outside any group, so "Add condition" never appears for it.
const buildProjectDocumentsFilters = (projectId, projectName) => {
  if (!projectId || !projectFilterPreset) {
    return [];
  }

  const projectFilter = {
    ...cloneDeep(projectFilterPreset),
    id: Random.id(),
    state: { include: [projectId] },
  };

  if (projectName) {
    projectFilter.meta = {
      ...cloneDeep(projectFilterPreset.meta),
      labels: { [projectId]: projectName },
    };
  }

  return [
    {
      id: Random.id(),
      type: FILTER_TYPE__ALL_OF,
      settings: {
        filters: [projectFilter],
      },
      meta: { nested: true },
    },
  ];
};

const getProjectDisplayName = (project) =>
  project.getName ? project.getName() : project.name;

const ProjectDataSubscription = ({
  projectId,
  onQuestionnaireNames,
  onSettled,
}) => {
  const projectSubscription = useDDPSubscription(
    projectId
      ? apiZedocOneProject.withParams({
          projectId,
        })
      : null,
  );
  useDDPSubscription(
    projectId ? projectMilestones.withParams({ projectId }) : null,
  );
  const { value: questionnaireNames } = useDDPQuery(
    projectId
      ? getQuestionnaireNames.withParams({
          projectId,
        })
      : null,
  );

  useEffect(() => {
    if (projectId && questionnaireNames) {
      onQuestionnaireNames(projectId, questionnaireNames);
    }
  }, [projectId, questionnaireNames, onQuestionnaireNames]);

  // "Settled" deliberately counts an errored subscription as done: a project
  // the viewer cannot read never yields a document, and callers waiting on the
  // document alone would wait forever.
  const settled = !!(projectSubscription?.ready || projectSubscription?.error);
  useEffect(() => {
    if (projectId) {
      onSettled(projectId, settled);
    }
  }, [projectId, settled, onSettled]);

  return null;
};

ProjectDataSubscription.propTypes = {
  projectId: PropTypes.string,
  onQuestionnaireNames: PropTypes.func.isRequired,
  onSettled: PropTypes.func.isRequired,
};

ProjectDataSubscription.defaultProps = {
  projectId: null,
};

const ConnectedFilters = ({ recipientId, extra }) => {
  const dispatch = useDispatch();
  const rawFilters = useSelector(getDocumentsFilters);
  const patientListDefaultAppliedRef = useRef(null);
  const lastRecipientIdForListDefaultRef = useRef(null);
  const [draftSelectedProjectIds, setDraftSelectedProjectIds] =
    useState(undefined);
  const [questionnaireNamesByProject, setQuestionnaireNamesByProject] =
    useState({});
  // Reported by ProjectDataSubscription, keyed by projectId. Deliberately not
  // reset when the patient changes: a subscription's readiness says nothing
  // about which patient we are viewing, and clearing it would strand any
  // project whose child is still mounted — that child's value hasn't changed,
  // so it would never report again and the entry would stay missing.
  const [settledProjectIds, setSettledProjectIds] = useState({});
  // Every filter write made here rather than by the user goes through this.
  // Each one mints fresh filter ids, which is what lets FiltersToolbar tell a
  // programmatic write apart from a user edit and remount <Filters> only for
  // the former — it reads `value` on mount only.
  const applyDocumentsFilters = useCallback(
    (nextFilters) => {
      dispatch(setDocumentsFilters(nextFilters));
    },
    [dispatch],
  );

  const participations = useSelector(
    ParticipationSelect.all().where({
      recipientId,
    }),
  );

  // The same publication PatientCard already subscribes to (identical params,
  // so this only bumps its refcount). We need its ready flag, not its data:
  // participations stream in one document at a time, so projectIds below grows
  // during load and a half-filled list is indistinguishable from a settled one.
  // Both defaulting effects have to wait for this before drawing conclusions
  // about which projects the patient is enrolled in.
  const { ready: participationsReady } = useDDPSubscription(
    patientDetails.withParams({
      recipientId,
    }),
  );

  const projectIds = useMemo(
    () => uniq(map(participations, 'projectId').filter(Boolean)),
    [participations],
  );

  const selectedProjectIds = useMemo(() => {
    if (draftSelectedProjectIds !== undefined) {
      return draftSelectedProjectIds;
    }
    return getSelectedProjectIds(rawFilters);
  }, [draftSelectedProjectIds, rawFilters]);

  const handleFiltersDraftChange = useCallback((filters) => {
    if (!hasProjectFilter(filters)) {
      setDraftSelectedProjectIds((previous) => {
        if (Array.isArray(previous) && previous.length === 0) {
          return previous;
        }
        return [];
      });
      return;
    }

    const ids = getSelectedProjectIds(filters);
    setDraftSelectedProjectIds((previous) => {
      const previousKey = (previous || []).join(',');
      const nextKey = ids.join(',');
      if (previousKey === nextKey) {
        return previous;
      }
      return ids;
    });
  }, []);

  const handleQuestionnaireNames = useCallback((projectId, names) => {
    setQuestionnaireNamesByProject((previous) => {
      if (previous[projectId] === names) {
        return previous;
      }
      return {
        ...previous,
        [projectId]: names,
      };
    });
  }, []);

  const handleProjectSettled = useCallback((projectId, settled) => {
    setSettledProjectIds((previous) => {
      if (!!previous[projectId] === settled) {
        return previous;
      }
      return {
        ...previous,
        [projectId]: settled,
      };
    });
  }, []);

  const projectIdsForPresets = useMemo(
    () => (selectedProjectIds.length > 0 ? selectedProjectIds : projectIds),
    [selectedProjectIds, projectIds],
  );

  const subscriptionProjectIds = useMemo(
    () => uniq([...projectIds, ...selectedProjectIds]),
    [projectIds, selectedProjectIds],
  );

  const allProjects = useSelector(ProjectSelect.all());
  // ProjectSelect.all() hands back documents in DDP arrival order — with no
  // sorter, createListSelector just returns values(docs). This list is both
  // the Project dropdown's option order and the source of the auto-selected
  // default below, so leaving it unsorted lets either differ between reloads
  // of the same patient. Sort by the displayed name to pin both down.
  const projects = useMemo(
    () =>
      sortBy(
        allProjects.filter((project) => projectIds.includes(project._id)),
        getProjectDisplayName,
      ),
    [allProjects, projectIds],
  );

  // Whether every enrolment's project subscription has come back — not whether
  // every project document arrived. Counting documents would wait forever on a
  // project the viewer has no read access to, and the defaults below would
  // never resolve.
  const projectsSettled = useMemo(
    () => projectIds.every((projectId) => settledProjectIds[projectId]),
    [projectIds, settledProjectIds],
  );

  const allMilestones = useSelector(ProjectMilestoneSelect.all());
  const milestonesForSelectedProjects = useMemo(() => {
    if (projectIdsForPresets.length === 0) {
      return [];
    }

    return allMilestones.filter(
      (milestone) =>
        !milestone.archived &&
        projectIdsForPresets.includes(milestone.getProjectId()),
    );
  }, [allMilestones, projectIdsForPresets]);

  const questionnaireNameById = useMemo(() => {
    const names = {};
    subscriptionProjectIds.forEach((projectId) => {
      Object.assign(names, questionnaireNamesByProject[projectId] || {});
    });
    return names;
  }, [subscriptionProjectIds, questionnaireNamesByProject]);

  const questionnairesForSelectedProjects = useMemo(() => {
    if (projectIdsForPresets.length === 0) {
      return [];
    }

    const identifiers = uniq(
      flatMap(
        allProjects.filter((project) =>
          projectIdsForPresets.includes(project._id),
        ),
        (project) => map(project.questionnaires || [], 'identifier'),
      ).filter(Boolean),
    );

    return sortBy(
      identifiers.map((identifier) => ({
        _id: identifier,
        name: questionnaireNameById[identifier] || identifier,
      })),
      'name',
    );
  }, [allProjects, projectIdsForPresets, questionnaireNameById]);

  const timezone = useMemo(() => {
    const projectId = projectIdsForPresets[0] || projectIds[0];
    const project = allProjects.find(({ _id }) => _id === projectId);
    return project && project.getTimezone ? project.getTimezone() : undefined;
  }, [allProjects, projectIdsForPresets, projectIds]);

  const presets = useMemo(() => {
    return map(defaultFilters, (preset) => {
      let next = preset;

      if (preset.settings && preset.settings.id === 'projectId') {
        next = setProjectJsonSchema(preset, projects);
      } else if (preset.settings && preset.settings.id === 'milestoneId') {
        next = setMilestoneJsonSchemaGroupedByProject(
          preset,
          milestonesForSelectedProjects,
          allProjects.filter((project) =>
            projectIdsForPresets.includes(project._id),
          ),
        );
      } else if (preset.settings && preset.settings.id === 'questionnaireId') {
        next = setQuestionnaireJsonSchema(
          preset,
          questionnairesForSelectedProjects,
        );
      } else if (preset.settings && preset.settings.id === 'completedAt') {
        next = setTimezone(preset, timezone);
      }

      return next;
    });
  }, [
    allProjects,
    projectIdsForPresets,
    projects,
    milestonesForSelectedProjects,
    questionnairesForSelectedProjects,
    timezone,
  ]);

  // Default project filter on the Documents tab:
  // - From a project-scoped screen: use the projectId stashed in localStorage.
  // - From the patient list (no stash): leave empty here; once participations
  //   load, the effect below selects the first project from the dropdown.
  useEffect(() => {
    setDraftSelectedProjectIds(undefined);
    setQuestionnaireNamesByProject({});
    patientListDefaultAppliedRef.current = null;
    const defaultProjectId = getDocumentsDefaultProjectId(recipientId);
    applyDocumentsFilters(
      defaultProjectId ? buildProjectDocumentsFilters(defaultProjectId) : [],
    );
  }, [recipientId, applyDocumentsFilters]);

  // Patient-list entry (no stashed projectId): once enrolments are known,
  // default the fixed Project row to the first project in the dropdown.
  //
  // This auto-default was tried once before and reverted, for two reasons
  // worth knowing. It makes apiZedocSearchResponses run unfiltered and then
  // again filtered, because participations only arrive after mount — the
  // 500ms debounce in usePagination (PatientDocuments.js) hides that only
  // when project data is already cached. It also makes the "Documents (N)"
  // tab label read higher than the list whenever a project is selected.
  // That label is deliberately a total across all of the patient's projects
  // (PatientCard derives it from patientRecords, not from these filters), so
  // the gap is expected — don't "fix" it by narrowing the count to match.
  useEffect(() => {
    const isNewRecipient =
      lastRecipientIdForListDefaultRef.current !== recipientId;
    lastRecipientIdForListDefaultRef.current = recipientId;
    if (isNewRecipient) {
      return;
    }
    // A stashed projectId already supplied this patient's default (mount
    // effect above), so mark it applied rather than just skipping. Clearing the
    // filter also clears the stash, which would otherwise let this effect fire
    // on the very next run and re-apply a project — making "Clear" look like it
    // did nothing, but only for patients reached from a project-scoped screen.
    if (getDocumentsDefaultProjectId(recipientId)) {
      patientListDefaultAppliedRef.current = recipientId;
      return;
    }
    if (patientListDefaultAppliedRef.current === recipientId) {
      return;
    }
    // Wait for the full enrolment list, then for every one of its project
    // subscriptions. Participations stream in and the per-project
    // subscriptions resolve in any order, so acting early would latch
    // whichever one happened to win the race rather than the first entry of
    // the settled list the dropdown shows — making the default differ between
    // reloads of the same patient.
    if (!participationsReady || isEmpty(projectIds) || !projectsSettled) {
      return;
    }
    if (getSelectedProjectIds(rawFilters).length > 0) {
      patientListDefaultAppliedRef.current = recipientId;
      return;
    }

    const firstProject = projects[0];
    if (!firstProject) {
      return;
    }

    patientListDefaultAppliedRef.current = recipientId;
    applyDocumentsFilters(
      buildProjectDocumentsFilters(
        firstProject._id,
        getProjectDisplayName(firstProject),
      ),
    );
  }, [
    recipientId,
    participationsReady,
    projectsSettled,
    projectIds,
    projects,
    rawFilters,
    applyDocumentsFilters,
  ]);

  // The stashed default is applied synchronously above, before we know
  // whether the patient is still enrolled in that project (or whether the
  // viewer still has access to it) — participations load separately. Once the
  // enrolment list has settled, correct a default that turns out stale:
  // without this, an unenrollment or access change would leave the Documents
  // tab silently stuck showing zero documents indefinitely, since the stored
  // default is otherwise only cleared by a manual filter change or a visit
  // from the (project-agnostic) Patients Directory.
  //
  // "Settled" has to mean participationsReady, not merely a non-empty
  // projectIds. On a cold cache — opening the patient in a new tab straight
  // from a project's patient list — the participations arrive one at a time,
  // so a partly-filled projectIds routinely lacks the very project we came
  // from. Judging staleness against that half-list discards the stash and
  // swaps the user onto whichever project loaded first. Detection needs
  // nothing beyond that; only choosing the replacement waits on the project
  // subscriptions, and it hands back to the patient-list default instead of
  // blocking when it cannot name one.
  //
  // lastRecipientIdRef guards against a same-commit race with the mount
  // effect above: when recipientId just changed, this effect's rawFilters
  // closure can still hold the PREVIOUS patient's filters (that effect's
  // dispatch hasn't propagated to a new render yet), which would almost
  // always look "stale" against the new patient's projectIds and wipe out
  // the default just applied. Skipping once and relying on rawFilters — a
  // dependency — to re-trigger this effect once it actually reflects the new
  // patient avoids that false positive.
  const lastRecipientIdRef = useRef(null);
  useEffect(() => {
    const isNewRecipient = lastRecipientIdRef.current !== recipientId;
    lastRecipientIdRef.current = recipientId;
    if (isNewRecipient || !participationsReady || isEmpty(projectIds)) {
      return;
    }
    const currentProjectIds = getSelectedProjectIds(rawFilters);
    const hasStaleProjectId = currentProjectIds.some(
      (projectId) => !projectIds.includes(projectId),
    );
    if (!hasStaleProjectId) {
      return;
    }
    clearDocumentsDefaultProjectId(recipientId);
    const firstProject = projectsSettled ? projects[0] : undefined;
    if (!firstProject) {
      // Can't name a replacement yet, so drop the stale selection and hand the
      // decision back to the patient-list default. That means releasing the
      // latch as well: the stash branch of that effect claimed it while the
      // now-cleared stash was still there, and leaving it claimed would strand
      // the mandatory Project row empty for good.
      patientListDefaultAppliedRef.current = null;
      applyDocumentsFilters([]);
      return;
    }
    patientListDefaultAppliedRef.current = recipientId;
    applyDocumentsFilters(
      buildProjectDocumentsFilters(
        firstProject._id,
        getProjectDisplayName(firstProject),
      ),
    );
  }, [
    participationsReady,
    projectsSettled,
    projectIds,
    projects,
    rawFilters,
    recipientId,
    applyDocumentsFilters,
  ]);

  return (
    <>
      {subscriptionProjectIds.map((projectId) => (
        <ProjectDataSubscription
          key={projectId}
          projectId={projectId}
          onQuestionnaireNames={handleQuestionnaireNames}
          onSettled={handleProjectSettled}
        />
      ))}
      <FiltersToolbar
        recipientId={recipientId}
        presets={presets}
        onFiltersDraftChange={handleFiltersDraftChange}
        extra={extra}
      />
    </>
  );
};

ConnectedFilters.propTypes = {
  recipientId: PropTypes.string.isRequired,
  extra: PropTypes.node,
};

ConnectedFilters.defaultProps = {
  extra: null,
};

export default ConnectedFilters;
